import bytes from 'bytes';
import { MAX_UPLOAD_SIZE, GENERIC_UPLOAD_ERROR } from 'constants/images';

// Remove the extension from a given file name
export function removeExtension(filename) {

  if (filename.indexOf('.') > -1) {
    return filename.slice(0, filename.lastIndexOf('.'));
  }

  return filename;
}

// Returns a function, that, as long as it continues to be invoked, will not be triggered.
export function debounce(func, wait) {
  var timeout, args, context, timestamp, result;

  var later = function() {
    var last = Date.now() - timestamp;

    if (last < wait && last >= 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      result = func.apply(context, args);
      context = args = null;
    }
  };

  function debounced() {
    context = this;
    args = arguments;
    timestamp = Date.now();
    if (!timeout) timeout = setTimeout(later, wait);

    return result;
  }

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
  }

  return debounced;
}

// Returns a function, that, when invoked, will only be triggered at most once during a given window of time.
export function throttle(func, wait) {
  var context, args, result;
  var timeout = null;
  var previous = 0;

  function later() {
    previous = Date.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  }

  return function() {
    var now = Date.now();
    if (!previous) previous = now;
    var remaining = wait - (now - previous);
    context = this;
    args = arguments;

    if (remaining <= 0 || remaining > wait) {

      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }

      previous = now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout) {
      timeout = setTimeout(later, remaining);
    }

    return result;
  };
}

// Taken from some minified code, so it's really really ugly, but it works
function getXproCurves(amount) {

  function getFixed(val) {
    return parseFloat((val * amount * (amount > 0 ? 1 : -1)).toFixed(2));
  }

  if (amount > 0) {
    const r = [0, .2, 0, -.1, 0].map(getFixed);
    const o = [0, .1, 0, -.1, 0].map(getFixed);
    const i = [.2, 0, .2].map(getFixed);
    const a = 0 + 2 / 3 * .5;
    const s = i[0] + 2 / 3 * (.5 - i[0]);
    const c = 1 + 2 / 3 * -0.5;
    const l = 1 - i[2] + 2 / 3 * (.5 - (1 - i[2]));

    return {
      red: [[0, 0], [.3, .3 - r[1]], [.7, .7 - r[3]], [1, 1]],
      green: [[0, 0], [.25, .25 - o[1]], [.75, .75 - o[3]], [1, 1]],
      blue: [[0, i[0]], [a, s], [c, l], [1, 1 - i[2]]]
    };
  }

  const f = [0, .2, .5, -.05].map(getFixed);
  const p = [0, -.15, 0, 0].map(getFixed);
  const d = [0, -.01, 0, .3].map(getFixed);

  return {
    red: [[0, 0], [.3, .3 - f[1]], [.7, .7 - f[2]], [.95, .95 - f[3]]],
    green: [[0, 0], [.25, .25 - p[1]], [.6, .6], [1, 1]],
    blue: [[0, 0], [.3, .3 - d[1]], [.5, .5], [1, 1 - d[3]]]
  };
}

export function applyFilters(filterCanvas, options) {
  const {
    blur,
    brightness = 0,
    contrast = 0,
    tint,
    hue = 0,
    saturation = 0,
    vignette,
    xpro
  } = options;

  // Add filters
  if (blur) {
    filterCanvas.lensBlur(blur / 100, 0, 0);
  }
  if (brightness || contrast) {
    filterCanvas.brightnessContrast(brightness / 100, contrast / 100);
  }
  if (tint) {
    filterCanvas.sepia(tint / 100);
  }
  if (hue || saturation) {
    filterCanvas.hueSaturation(hue / 100, saturation / 100);
  }
  if (vignette) {
    filterCanvas.vignette(0.1, vignette / 100);
  }
  if (xpro) {
    const curves = getXproCurves(xpro / 100);
    filterCanvas.curves(curves.red, curves.green, curves.blue);
  }

  return filterCanvas;
}

// Remove slashes from a URL to reveal only the most specific portion
export function simplifyUrl(input) {
  const url = input.split('?')[0].split('#')[0];
  const lastSlash = url.lastIndexOf('/');

  // If there is a slash, drop everything but the last part
  if (lastSlash > -1) {
    return url.slice(lastSlash + 1);
  }

  return url;
}

// Get a pseudo-random ID, could have a higher than desired chance of collision
export function getId() {
  return Math.random().toString(36).slice(2);
}

// Given a file that failed to upload, determine which error message to send to the user
export function getUploadedImageErrorMessage(file) {

  if (file.type && file.type.indexOf('image') < 0) {
    return `${file.name} is not a supported image type`;
  }

  if (file.size > MAX_UPLOAD_SIZE) {
    return `${file.name} exceeds the maximum image size. Image must be smaller than ${bytes(MAX_UPLOAD_SIZE)}.`;
  }

  return GENERIC_UPLOAD_ERROR;
}

// Get the image type of a url by slicing the name, this is not guaranteed to be accurate
export function getUrlImageType(input) {
  const url = input.split('?')[0].split('#')[0];
  const lastPeriod = url.lastIndexOf('.');
  let fileExtension;

  if (lastPeriod > -1) {
    fileExtension = url.slice(lastPeriod).replace(/[^A-Za-z0-9]/, '');
  } else {
    fileExtension = url;
  }

  // Normalize jpeg file extensions
  if (fileExtension === 'jpg') {
    return 'jpeg';
  }

  return fileExtension;
}

// Throw an error if the response of a process has any errors
export function validateProcessResponse(response) {
  const errors = [];

  response.results.forEach((result, idx) => {
    if (result.errorMessage) {
      errors.push(`${idx + 1}. ${result.errorMessage}`);
    }
  });

  if (errors.length) {
    throw new Error(errors.join('\n'));
  }

  return response;
}
// Throw an error if the response of a process has any errors
export function getLocales(key) {
  const l = {
    'en': 'English',
    'bn': 'Bangla',
    'xy': 'XYZ'
  }
  if(key) {
    return l[key];
  } else {
    return l;
  }
}



// WEBPACK FOOTER //
// ./src/utils/index.js